import { ReactElement, useEffect } from 'react'
import { FormattedMessage, defineMessages, useIntl } from 'react-intl'
import { getIn, useFormikContext } from 'formik'
import { isBefore } from 'date-fns'
import styled from 'styled-components'

import { Column } from 'components/v2/Column'
import FormLayout from 'components/v2/FormLayout'
import Heading from 'components/v2/Heading'
import { InputDate } from 'components/v2/InputDate'
import { SelectActivePeriod } from 'components/v2/SelectActivePeriod'
import { Row } from 'components/v2/Row'
import { SelectSalesChannelsV2 } from 'features/Services/components/SelectSalesChannelsV2'
import { IOfferSettingsCardProps, IOfferSettingsForm, ISalesChannel } from './types'
import { Label } from 'components/v2/Label'
import { Radio } from 'components/v2/Radio'
import { ErrorMessage } from 'components/v2/ErrorMessage'
import { GTCompanyV2Vertical, GTRouteServiceCompanyVertical } from 'gql/types.generated'
import { MultiBusinessClientAutocomplete } from 'features/BusinessClients/components/MultiBusinessClientAutocomplete'
import { IRouteForm } from 'features/Services/components/NewServicesV2/components/RouteCard/types'
import { MultiBusinessSiteAutocomplete } from 'features/BusinessSites/components/MultiBusinessSiteAutocomplete'
import { isEmpty } from 'lodash'
import { APP_MX_SALES_CHANNEL, APP_MX_VERTICALS, DEFAULT_SALES_CHANNELS } from 'models/services'
import { useSalesChannelAutocomplete, useSalesChannelsAutocomplete } from 'features/Services/hooks'

const messages = defineMessages({
  startDateLabel: {
    id: 'services.form.startDate.label',
    defaultMessage: 'Start date'
  },
  initialStartDateLabel: {
    id: 'services.form.initialStartDate.label',
    defaultMessage: 'Initial start date'
  },
  endDateLabel: {
    id: 'services.form.endDate.label',
    defaultMessage: 'End date'
  },
  salesChannelsLabel: {
    id: 'services.form.salesChannels.label',
    defaultMessage: 'Sales channels'
  },
  salesChannelsPlaceholder: {
    id: 'services.form.salesChannels.placeholder',
    defaultMessage: 'Select sales channels'
  },
  businessClientsPlaceholder: {
    id: 'services.form.businessClients.placeholder',
    defaultMessage: 'Select business clients'
  },
  businessSitesPlaceholder: {
    id: 'services.form.businessSites.placeholder',
    defaultMessage: 'Select business sites'
  }
})

const TitleContainer = styled.div<TSFixMe>`
  align-items: center;
  display: flex;
  justify-content: space-between;
`
const RadioWrapper = styled.div`
  display: flex;
  gap: 64px;
  flex-wrap: wrap;

  input {
    margin-left: 0;
  }
`

export const OfferSettingsCard = ({ isEdit, vertical }: IOfferSettingsCardProps): ReactElement => {
  const intl = useIntl()
  const { values, setFieldValue, setValues, handleBlur, touched, errors } = useFormikContext<
    IOfferSettingsForm & IRouteForm
  >()

  useEffect(() => {
    if (vertical === GTRouteServiceCompanyVertical.Travel) {
      setFieldValue('isPrivate', false)
    } else if (vertical === GTRouteServiceCompanyVertical.Rental || vertical === GTRouteServiceCompanyVertical.Team) {
      setFieldValue('isPrivate', true)
    }
  }, [vertical])

  const isShowingBusinessClients = vertical === GTRouteServiceCompanyVertical.City && values.isPrivate
  const isShowingSalesChannels = vertical !== GTRouteServiceCompanyVertical.Rental
  const appMxSalesChannel = useSalesChannelAutocomplete(APP_MX_SALES_CHANNEL.id)
  const defaultSalesChannels = useSalesChannelsAutocomplete(DEFAULT_SALES_CHANNELS.map(({ id }) => id))
  const isServiceTypeDisabled = isEdit || vertical !== GTRouteServiceCompanyVertical.City

  useEffect(() => {
    if (!isEdit) {
      //@ts-expect-error FIXME GTCompanyV2Vertical doesn't match APP_MX_VERTICALS
      if (APP_MX_VERTICALS.includes(vertical as unknown as GTCompanyV2Vertical)) {
        setFieldValue('salesChannels', [appMxSalesChannel])
      } else if (GTCompanyV2Vertical.Travel === (vertical as unknown as GTCompanyV2Vertical)) {
        setFieldValue('salesChannels', defaultSalesChannels)
      }
    }
  }, [vertical])

  return (
    <FormLayout.Card>
      <TitleContainer>
        <Heading.H4>
          <FormattedMessage id="services.form.section.offerSettings.title" defaultMessage="Offer Settings" />
        </Heading.H4>
      </TitleContainer>
      <Row noMargin>
        <Column width="50%">
          <Label>
            <FormattedMessage id="routes.new.form.serviceType.label" defaultMessage="Type" />
          </Label>
          <RadioWrapper>
            <Radio
              name="serviceType"
              checked={values.isPrivate === false}
              onChange={(): void => {
                setValues({
                  ...values,
                  isPrivate: false,
                  businessClients: [],
                  businessSites: []
                })
              }}
              disabled={isServiceTypeDisabled}
            >
              <FormattedMessage id="routes.new.form.serviceType.public.label" defaultMessage="Public" />
            </Radio>
            <Radio
              name="serviceType"
              checked={values.isPrivate === true}
              onChange={(): void => {
                setValues({
                  ...values,
                  isPrivate: true,
                  businessClients: [],
                  businessSites: []
                })
              }}
              disabled={isServiceTypeDisabled}
            >
              <FormattedMessage id="routes.new.form.serviceType.private.label" defaultMessage="Private" />
            </Radio>
          </RadioWrapper>
          {touched.isPrivate && errors.isPrivate && <ErrorMessage>{errors.isPrivate}</ErrorMessage>}
        </Column>
        <Column width="50%">
          <Label>
            <FormattedMessage id="routes.new.form.tolls.label" defaultMessage="Tolls" />
          </Label>
          <RadioWrapper>
            <Radio
              name="hasTolls"
              checked={values.hasTolls}
              onChange={(): void => {
                setFieldValue('hasTolls', true)
              }}
            >
              <FormattedMessage id="routes.new.form.hasTolls.label" defaultMessage="Yes" />
            </Radio>
            <Radio
              name="hasTolls"
              checked={!values.hasTolls}
              onChange={(): void => {
                setFieldValue('hasTolls', false)
              }}
            >
              <FormattedMessage id="routes.new.form.hasNotTolls.outbound.label" defaultMessage="No" />
            </Radio>
          </RadioWrapper>
          {touched.hasTolls && errors.hasTolls && <ErrorMessage>{errors.hasTolls}</ErrorMessage>}
        </Column>
      </Row>
      {isShowingBusinessClients && (
        <Row noMargin>
          <Column>
            <MultiBusinessClientAutocomplete
              name="businessClients"
              label={<FormattedMessage id="services.form.businessClients.label" defaultMessage="Business clients" />}
              placeholder={intl.formatMessage(messages.businessClientsPlaceholder)}
              values={values.businessClients}
              onChange={(businessClients): void => {
                setValues({
                  ...values,
                  businessClients,
                  businessSites: values.businessSites.filter((site) => {
                    return businessClients.some((client) => client.id === site.clientId)
                  })
                })
              }}
              onBlur={handleBlur}
              touched={getIn(touched, 'businessClients')}
              errors={getIn(errors, 'businessClients')}
              companyId={values.company.id}
              disabled={!values.company.id}
            />
          </Column>
          <Column>
            <MultiBusinessSiteAutocomplete
              name="businessSites"
              label={<FormattedMessage id="services.form.businessSites.label" defaultMessage="Business sites" />}
              placeholder={intl.formatMessage(messages.businessSitesPlaceholder)}
              values={values.businessSites}
              onChange={(values): void => {
                setFieldValue('businessSites', values)
              }}
              onBlur={handleBlur}
              touched={getIn(touched, 'businessSites')}
              errors={getIn(errors, 'businessSites')}
              optional
              disabled={isEmpty(values.businessClients)}
              businessClientIds={values.businessClients.map(({ id }) => id)}
            />
          </Column>
        </Row>
      )}
      <Row noMargin>
        <Column width="50%" noMargin>
          <InputDate
            id="startDate"
            name="startDate"
            intl={intl}
            label={intl.formatMessage(isEdit ? messages.initialStartDateLabel : messages.startDateLabel)}
            placeholder="--/--/----"
            value={values.startDate}
            onChange={async (value): Promise<void> => {
              setValues({
                ...values,
                startDate: value as Date,
                endDate: values.endDate && isBefore(values.endDate, value as Date) ? value : values.endDate
              })
            }}
            disabledDays={{
              before: new Date()
            }}
            touched={getIn(touched, 'startDate')}
            errors={getIn(errors, 'startDate')}
            disabled={isEdit}
          />
        </Column>
        <Column noMargin>
          <InputDate
            id="endDate"
            name="endDate"
            intl={intl}
            label={intl.formatMessage(messages.endDateLabel)}
            placeholder="--/--/----"
            value={values.endDate}
            onChange={async (value): Promise<void> => {
              setValues({
                ...values,
                activePeriod: '',
                endDate: value,
                startDate: value && isBefore(value, values.startDate) ? value : values.startDate
              })
            }}
            disabledDays={{
              before: new Date()
            }}
            touched={getIn(touched, 'endDate')}
            errors={getIn(errors, 'endDate')}
            optional
            disabled={isEdit}
          />
        </Column>
      </Row>
      <Row noMargin>
        <Column noMargin>
          <SelectActivePeriod
            value={values?.activePeriod ?? ''}
            onChange={async (activePeriod): Promise<void> => {
              setValues({
                ...values,
                activePeriod,
                endDate: undefined
              })
            }}
            touched={getIn(touched, 'activePeriod')}
            errors={getIn(errors, 'activePeriod')}
            disabled={isEdit && Boolean(values.endDate)}
          />
        </Column>
      </Row>
      {isShowingSalesChannels && (
        <Row noMargin>
          <Column width="50%" noMargin>
            <SelectSalesChannelsV2
              id="salesChannels"
              label={intl.formatMessage(messages.salesChannelsLabel)}
              placeholder={intl.formatMessage(messages.salesChannelsPlaceholder)}
              values={values.salesChannels}
              onChange={(values: ISalesChannel[]): void => {
                setFieldValue('salesChannels', values)
              }}
              onBlur={handleBlur}
              touched={getIn(touched, 'salesChannels')}
              errors={getIn(errors, 'salesChannels')}
              //@ts-expect-error FIXME GTCompanyV2Vertical doesn't match APP_MX_VERTICALS
              disabled={APP_MX_VERTICALS.includes(vertical as unknown as GTCompanyV2Vertical)}
            />
          </Column>
        </Row>
      )}
    </FormLayout.Card>
  )
}
